import type { CSSProperties, ReactNode } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  cn,
} from "@nexus/ui";

/**
 * Floor that fits the org tab row on one line (Danger Zone + count badges).
 * Identity Hub exception to DESIGN.md `drawer-right` 720px — 720 cannot fit 8 pills.
 * `max-w-none` is required so twMerge drops SheetContent's `max-w-[720px]`.
 */
export const IAM_OVERLAY_DRAWER_MIN_WIDTH_PX = 920;

const IAM_OVERLAY_MIN_WIDTH_VAR = "--iam-overlay-min-width";

/** ~40vw when wider than the tab-row floor; never past the viewport. */
export const IAM_OVERLAY_DRAWER_WIDTH_CLASS =
  "w-[min(100vw,max(40vw,var(--iam-overlay-min-width)))] max-w-none p-0";

function overlayWidthStyle(minWidthPx: number, extra?: CSSProperties): CSSProperties {
  return {
    ...extra,
    [IAM_OVERLAY_MIN_WIDTH_VAR]: `${minWidthPx}px`,
  } as CSSProperties;
}

export interface IamOverlayDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  showClose?: boolean;
  /** Width floor before the ~40vw rule applies. Defaults to the org tab-row floor. */
  minWidthPx?: number;
}

/**
 * Full-height right overlay used by Identity Hub row-click drawers.
 * Header chrome stays in the child; this shell only owns Sheet + width.
 */
export function IamOverlayDrawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  style,
  showClose = false,
  minWidthPx = IAM_OVERLAY_DRAWER_MIN_WIDTH_PX,
}: IamOverlayDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showClose={showClose}
        style={overlayWidthStyle(minWidthPx, style)}
        className={cn(
          "flex h-full flex-col overflow-hidden bg-card",
          IAM_OVERLAY_DRAWER_WIDTH_CLASS,
          className,
        )}
      >
        <SheetTitle className="sr-only">{title}</SheetTitle>
        {description ? (
          <SheetDescription className="sr-only">{description}</SheetDescription>
        ) : null}
        {children}
      </SheetContent>
    </Sheet>
  );
}
